import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllNotifications, markNotificationAsReadAPI } from 'services/user_api'; // Assuming these API functions are available

const initialState = {
  unreadCount: 0,  // Track unread notifications count
  unreadMessageCount: 0,
  notifications: [],  // Array to store notifications
  loading: false,  // Track loading state
  error: null,  // To store errors from API calls
  pageconfig: {
    totalCount: 0,  // Total number of notifications
    page: 1,  // Current page
    limit: 1000,  // Items per page
    totalPages: 0,  // Total number of pages
  },
};

// Fetch notifications
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (payload = {}, { rejectWithValue, getState, dispatch }) => {
    const { pageconfig } = getState().notifications;
    try {
      const response = await getAllNotifications({
        page: pageconfig.page,   // Current page
        limit: pageconfig.limit  // Items per page
      });
      return response.response; // Response contains notifications
    } catch (error) {
      return rejectWithValue(error.message);  // Return error if request fails
    }
  }
);

// Mark notification as read
export const markNotificationAsReadThunk = createAsyncThunk(
  'notifications/markNotificationAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await markNotificationAsReadAPI(notificationId);
      if (!response.status) throw new Error('Failed to mark notification as read');
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Set unread notification count
    setUnreadCount(state, action) {
      state.unreadCount = action.payload;
    },

    setUnreadMessageCount(state, action) {
      state.unreadMessageCount = action.payload;
    },


    // Set notifications array
    setNotifications(state, action) {
      state.notifications = action.payload;
    },

    // Mark a specific notification as read
    markNotificationAsRead(state, action) {
      const notificationId = action.payload;
      // console.log("notificationId", notificationId);
      // console.log("state.notifications", state.notifications);
     
    },

    // Set page configuration for pagination
    setPageConfig(state, action) {
      state.pageconfig = action.payload;
    },

    // Update a specific notification's data
    updateNotification(state, action) {
      const updatedNotification = action.payload;
      const notificationIndex = state.notifications.findIndex(
        (notification) => notification._id === updatedNotification._id
      );
      if (notificationIndex !== -1) {
        state.notifications[notificationIndex] = {
          ...state.notifications[notificationIndex],
          ...updatedNotification,  // Merge the updated data
        };
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Handle fetchNotifications async thunk
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;

        // Merge notifications if the page > 1
        if (action.payload.currentPage > 1) {
            state.notifications = [...state.notifications, ...action.payload.notifications];
        } else {
            // If it's the first page, simply set the notifications
            state.notifications = action.payload.notifications;
        }

        // Update unread count
        state.unreadCount = action.payload.unreadCount;
        state.unreadMessageCount = action.payload.unreadMessageCount;

        // Update pagination details
        state.pageconfig = {
            ...state.pageconfig,
            totalCount: action.payload.totalNotifications,
            page: action.payload.currentPage,
            totalPages: action.payload.totalPages
        };
        })

      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;  // Store error if failed
      })

      // Handle markNotificationAsReadThunk async thunk
      .addCase(markNotificationAsReadThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(markNotificationAsReadThunk.fulfilled, (state, action) => {
        state.loading = false;
        const notificationId = action.payload;
        // console.log("notificationId", notificationId);
        // console.log("state.notifications", state.notifications);
        state.notifications = state.notifications.map((notification) => {
          if (notification?._id === notificationId && notification?.status !== 'read') {
            return { ...notification, status: 'read' };
          }
          return notification;
        });
        // Optionally update unread count based on the read status
        state.unreadCount -= 1;
      })
      .addCase(markNotificationAsReadThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setUnreadCount, setNotifications, markNotificationAsRead, setPageConfig, updateNotification } = notificationSlice.actions;

export default notificationSlice.reducer;
